import { forwardRef, useCallback, useState } from "react"
import { NodeSelection } from "@tiptap/pm/state"
import type { Editor } from "@tiptap/react"

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"

// --- Lib ---
import { isExtensionAvailable, isNodeTypeSelected } from "@/lib/tiptap-utils"

// --- API ---
import { rotateNoteImage } from "@/api/services/memo"

// --- UI Primitives ---
import type { ButtonProps } from "@/components/tiptap-ui-primitive/button"
import { Button } from "@/components/tiptap-ui-primitive/button"

// --- Icons ---
import { RotateCwIcon } from "@/components/tiptap-icons/rotate-cw-icon"
import { RotateCcwIcon } from "@/components/tiptap-icons/rotate-ccw-icon"

/** ノート画像URL (/api/memos/images/{public_id}/file) から public_id を抽出する。 */
const extractNotePublicId = (src: string | undefined | null): string | null => {
  if (!src) return null
  const match = src.match(/\/memos\/images\/([^/?#]+)\/file(?:$|[?#])/)
  return match ? (match[1] ?? null) : null
}

interface SelectedImage {
  pos: number
  src: string
}

const getSelectedImage = (editor: Editor | null): SelectedImage | null => {
  if (!editor) return null
  const { selection } = editor.state
  if (!(selection instanceof NodeSelection)) return null
  const node = selection.node
  if (node.type.name !== "image") return null
  const src = node.attrs.src as string | undefined
  if (!src) return null
  return { pos: selection.from, src }
}

export interface ImageRotateButtonProps extends Omit<ButtonProps, "type"> {
  /** Tiptap editor instance (省略時は provider から取得) */
  editor?: Editor | null
  /** 時計回りの回転角度 (負値で反時計回り) */
  degrees?: number
  /** ボタン非表示にしたい条件 */
  hideWhenUnavailable?: boolean
}

/**
 * ノート画像を回転するボタン。
 *
 * サーバー側で画像ファイル自体を回転して上書き保存し、revision 属性を更新して
 * cache-busting する。同時に width をリセットし、90/270° 回転時の
 * アスペクト比崩れを防ぐ（画像側のサイズに追従させる）。
 */
export const ImageRotateButton = forwardRef<
  HTMLButtonElement,
  ImageRotateButtonProps
>(
  (
    {
      editor: providedEditor,
      degrees = 90,
      hideWhenUnavailable = false,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor)
    const [busy, setBusy] = useState(false)

    const isEditable = !!editor?.isEditable
    const hasImage = editor
      ? isExtensionAvailable(editor, ["image"]) &&
        isNodeTypeSelected(editor, ["image"])
      : false

    const label = degrees > 0 ? "右に90°回転" : "左に90°回転"
    const Icon = degrees > 0 ? RotateCwIcon : RotateCcwIcon

    const handleClick = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        if (!editor || busy) return

        const selected = getSelectedImage(editor)
        if (!selected) return
        const publicId = extractNotePublicId(selected.src)
        if (!publicId) return

        setBusy(true)
        try {
          const res = await rotateNoteImage(publicId, degrees)
          const rotatedByOdd = Math.abs(degrees) % 180 !== 0
          const attrs: Record<string, unknown> = { revision: res.revision }
          // 90/270° 回転で幅高さが入れ替わるため width をクリアして自然サイズに戻す
          if (rotatedByOdd) {
            attrs.width = null
            attrs.height = null
          }
          editor
            .chain()
            .focus()
            .setNodeSelection(selected.pos)
            .updateAttributes("image", attrs)
            .run()
        } catch (err) {
          console.error("Failed to rotate image:", err)
        } finally {
          setBusy(false)
        }
      },
      [editor, busy, degrees, onClick]
    )

    if (!editor) return null
    if (hideWhenUnavailable && (!isEditable || !hasImage)) return null

    const disabled = !isEditable || !hasImage || busy

    return (
      <Button
        type="button"
        variant="ghost"
        data-active-state="off"
        role="button"
        tabIndex={-1}
        disabled={disabled}
        data-disabled={disabled}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? <Icon className="tiptap-button-icon" />}
      </Button>
    )
  }
)

ImageRotateButton.displayName = "ImageRotateButton"
